import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import RoleService from '../../services/roleService';
import UserService from '../../services/userService';
import ToastContainer from '../common/ToastContainer';
import { useToast } from '../../hooks/useToast';
import { useAuthSync } from '../../hooks/useAuthSync';
import { MEDICAL_DATA_PERMISSION } from '../../utils/medicalData';
import './RoleForm.css';

const PERMISSION_GROUPS = [
  {
    title: 'Reportes',
    options: [
      { value: 'reports:viewfinancial', label: 'Ver Reportes Financieros' },
      { value: 'reports:viewappointments', label: 'Ver Reportes de Citas' }
    ]
  },
  {
    title: 'Pacientes',
    options: [
      { value: 'patient:viewmedicaldata', label: 'Ver Datos Medicos del Paciente' },
      { value: 'patient:viewcontactdetails', label: 'Ver Datos de Contacto del Paciente' }
    ]
  },
  {
    title: 'Agenda',
    options: [
      { value: 'agenda:viewall', label: 'Ver Todas las Agendas' },
      { value: 'appointments:createforothers', label: 'Crear citas para otros' }
    ]
  },
  {
    title: 'Planes de Tratamiento',
    options: [
      { value: 'treatmentplans:viewall', label: 'Ver todos los Planes de Tratamiento' }
    ]
  }
];

const RoleForm = () => {
  const { roleId } = useParams();
  const isEdit = useMemo(() => Boolean(roleId), [roleId]);
  const navigate = useNavigate();
  const auth = useAuth();
  const { toasts, removeToast, error: showError } = useToast();
  const { isMedicalDataEnabled } = useAuthSync();
  const medicalDataEnabled = isMedicalDataEnabled();
  // Hidden options keep their stored value, so re-enabling medical data restores existing grants
  const permissionGroups = useMemo(() => (
    medicalDataEnabled
      ? PERMISSION_GROUPS
      : PERMISSION_GROUPS.map(group => ({
        ...group,
        options: group.options.filter(option => option.value !== MEDICAL_DATA_PERMISSION)
      }))
  ), [medicalDataEnabled]);

  const [formData, setFormData] = useState({
    name: '',
    permissions: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roleUsers, setRoleUsers] = useState([]);

  useEffect(() => {
    const loadRole = async () => {
      if (!isEdit) {
        setIsLoading(false);
        return;
      }

      try {
        const token = auth.user?.access_token;
        const role = await RoleService.getRoleById(roleId, token);
        if (!role) {
          showError('Rol no encontrado');
          setIsLoading(false);
          return;
        }

        setFormData({
          name: role.name || '',
          permissions: role.permissions || []
        });

        const users = await UserService.getUsersByRoleId(roleId, token);
        setRoleUsers(users || []);
      } catch (error) {
        showError('Error al cargar el rol');
      } finally {
        setIsLoading(false);
      }
    };

    if (auth.isAuthenticated) {
      loadRole();
    }
  }, [auth.isAuthenticated, auth.user?.access_token, isEdit, roleId, showError]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePermissionToggle = (permissionValue) => {
    setFormData((prev) => {
      const updated = new Set(prev.permissions);
      if (updated.has(permissionValue)) {
        updated.delete(permissionValue);
      } else {
        updated.add(permissionValue);
      }
      return {
        ...prev,
        permissions: Array.from(updated)
      };
    });
  };

  const handleBack = () => {
    navigate('/settings/roles');
  };

  const handleDeleteRole = async () => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este rol?')) {
      return;
    }

    try {
      setIsSubmitting(true);
      const token = auth.user?.access_token;
      await RoleService.deleteRole(roleId, token);
      navigate('/settings/roles', {
        state: {
          message: 'Rol eliminado correctamente',
          messageId: `role-deleted-${Date.now()}`
        }
      });
    } catch (error) {
      showError('No se pudo eliminar el rol');
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.name.trim()) {
      showError('El nombre del rol es obligatorio');
      return;
    }

    try {
      setIsSubmitting(true);
      const token = auth.user?.access_token;
      const payload = {
        name: formData.name.trim(),
        permissions: formData.permissions
      };

      if (isEdit) {
        await RoleService.updateRole(roleId, payload, token);
        navigate('/settings/roles', {
          state: {
            message: 'Rol actualizado correctamente',
            messageId: `role-updated-${Date.now()}`
          }
        });
      } else {
        await RoleService.createRole(payload, token);
        navigate('/settings/roles', {
          state: {
            message: 'Rol creado correctamente',
            messageId: `role-created-${Date.now()}`
          }
        });
      }
    } catch (error) {
      showError(isEdit ? 'No se pudo actualizar el rol' : 'No se pudo crear el rol');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="role-form-container loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="role-form-container">
      <div className="role-form-header">
        <div>
          <h1>{isEdit ? 'Editar Rol' : 'Nuevo Rol'}</h1>
          <p>Define permisos y tipo de rol para el equipo</p>
        </div>
        <button className="btn-secondary" onClick={handleBack}>
          Volver
        </button>
      </div>

      <form onSubmit={handleSubmit} className="role-form">
        <div className="role-form-row">
          <div className="role-field">
            <label htmlFor="name">
              Nombre del rol <span className="required">*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Ej. Asistente"
              required
            />
          </div>
        </div>

        <div className="role-permissions">
          <h3>Permisos</h3>
          <div className="permissions-grid">
            {permissionGroups.map((group) => (
              <div key={group.title} className="permission-group">
                <h4>{group.title}</h4>
                <div className="permission-options">
                  {group.options.map((permission) => (
                    <label key={permission.value} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.permissions.includes(permission.value)}
                        onChange={() => handlePermissionToggle(permission.value)}
                      />
                      {permission.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <div className="form-actions-right">
            <button className="btn-secondary" type="button" onClick={handleBack}>
              Cancelar
            </button>
            <button className="btn-primary" type="submit" disabled={isSubmitting}>
              {isEdit ? 'Guardar cambios' : 'Crear rol'}
            </button>
          </div>
        </div>
      </form>

      {isEdit && (
        <div className="role-users-section">
          <div className="role-users-section-header">
            <h3>Usuarios con este rol</h3>
            {roleUsers.length === 0 && (
              <button
                className="btn-danger"
                type="button"
                onClick={handleDeleteRole}
                disabled={isSubmitting}
              >
                Eliminar rol
              </button>
            )}
          </div>
          {roleUsers.length === 0 ? (
            <p className="no-users-message">No hay usuarios asignados a este rol</p>
          ) : (
            <div className="role-users-list">
              {roleUsers.map((user) => (
                <div key={user.id} className="role-user-item">
                  <span className="role-user-name">{user.name || user.email}</span>
                  <span className="role-user-email">{user.email}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default RoleForm;
